import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  Sun,
  Moon
} from 'lucide-react';
import { BackgroundGenerationIndicator } from '@/components/learning/BackgroundGenerationIndicator';
import NotificationBell from './NotificationBell';

interface AppHeaderProps {
  onMenuClick: () => void;
}

export default function AppHeader({ onMenuClick }: AppHeaderProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage
  useEffect(() => {
    const isDark = localStorage.getItem('theme') === 'dark' ||
      (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-tutara-dark-mode-surface border-b border-gray-200 dark:border-tutara-dark-mode-border">
      <div className="h-16 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full">
          {/* Left Section */}
          <div className="flex items-center">
            {/* Mobile Menu Button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg"
            >
              <Menu className="h-6 w-6 text-tutara-charcoal dark:text-white" />
            </button>

            {/* Logo */}
            <Link to="/dashboard" className="flex items-center ml-4 lg:ml-0">
              <img
                src="/logos/logo-primary.png?v=2"
                alt="Tutara"
                className="h-8 w-auto block dark:hidden"
              />
              <img
                src="/logos/logo-white.png?v=2"
                alt="Tutara"
                className="h-8 w-auto hidden dark:block"
              />
            </Link>
          </div>

          {/* Right Section - Minimal */}
          <div className="flex items-center space-x-2">
            {/* Background Course Generation Indicator */}
            <BackgroundGenerationIndicator />

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg transition-colors"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Notifications */}
            <NotificationBell viewAllTo="/notifications" />
          </div>
        </div>
      </div>
    </header>
  );
}
